import { Dispatch, FC, SetStateAction } from 'react';
import SupportIcon from '../../assets/support.svg';
import TargetIcon from '../../assets/target.svg';
import { SelectedSkillIndividualReport } from './IndividualReportWrapper';
import { Calculator, ChevronRight, CornerDownRight, PencilRuler, SquarePlay } from 'lucide-react';
import { Tooltip } from 'primereact/tooltip';
import { IndividualStudentReportStatsQuery$data } from '../../graphQLOperations/__generated__/IndividualStudentReportStatsQuery.graphql';

type DailyActiveSkillType =
  IndividualStudentReportStatsQuery$data['individualReportDailyActivity'][0]['dailyActivitySkills'][0];
type InferredSkillType = DailyActiveSkillType['skillInferredSkills'][0];

interface InferredSkill extends InferredSkillType {}
interface DailyActivitySkill extends DailyActiveSkillType {
  readonly skillInferredSkills: ReadonlyArray<InferredSkill>;
}
interface Props {
  skill: DailyActivitySkill;
  setSelectedSkill: Dispatch<SetStateAction<SelectedSkillIndividualReport | null>>;
  setSelectedModalSkill: Dispatch<SetStateAction<{ code: string } | null>>;
}

const IndividualReportDailySkill: FC<Props> = ({ skill, setSelectedSkill, setSelectedModalSkill }) => {
  const getColorScore = (score: number) => {
    if (score >= 8) {
      return 'tw-bg-emerald-100';
    } else if (score === 6) {
      return 'tw-bg-amber-100';
    } else {
      return 'tw-bg-red-100';
    }
  };
  return (
    <div key={skill.code} className="tw-my-2 tw-w-full">
      <div className="tw-w-full tw-flex tw-items-center tw-border-solid tw-border-1 tw-border-neutral-100 tw-rounded-lg tw-px-2 tw-mb-2">
        <img className="tw-h-[1.5rem] tw-w-[1.5rem] tw-mr-2" src={skill.isTargetSkill ? TargetIcon : SupportIcon}></img>
        <div className="tw-w-[40%]">
          <p
            className="tw-underline tw-text-[1.05rem] cursor-pointer tw-text-blue-600"
            onClick={() => {
              setSelectedModalSkill({ code: skill.code });
            }}
          >
            {skill.name} {`(${skill.code})`}
          </p>
        </div>
        <div className="tw-w-[20%] tw-flex tw-items-center tw-text-neutral-800 tw-justify-between">
          <SquarePlay size={20} id={`workedExmapleVideoISR${skill.code}`} />

          <Tooltip position="top" target={`#workedExmapleVideoISR${skill.code}`}>
            <div>
              <p className="tw-text-center tw-text-neutral-300">
                <b>Worked Example Video</b>
              </p>
            </div>
          </Tooltip>

          <span className="tw-text-neutral-800 tw-font-semibold tw-w-[3rem] tw-text-center">
            {skill.exampleVideoTime + ' m'}
          </span>
          <span className="tw-text-neutral-400 tw-text-xl tw-mr-4">|</span>
          <Calculator size={20} id={`practiceProblemsISR${skill.code}`} />
          <Tooltip position="top" target={`#practiceProblemsISR${skill.code}`}>
            <div>
              <p className="tw-text-center tw-text-neutral-300">
                <b>Practice problems</b>
              </p>
            </div>
          </Tooltip>
          <span className="tw-text-neutral-800 tw-font-semibold tw-w-[3rem] tw-text-center">
            {skill.practiceProblemTime + ' m'}
          </span>
          <span className="tw-text-neutral-400 tw-text-xl tw-mr-4">|</span>
          <PencilRuler size={20} id={`assessmentTimeISR${skill.code}`} />
          <Tooltip position="top" target={`#assessmentTimeISR${skill.code}`}>
            <div>
              <p className="tw-text-center tw-text-neutral-300">
                <b>Skill challenges</b>
              </p>
            </div>
          </Tooltip>
          <span className="tw-text-neutral-800 tw-font-semibold tw-w-[3rem] tw-text-center">
            {skill.skillChallengesTime + ' m'}
          </span>
        </div>
        <div className="tw-flex tw-items-center tw-justify-end tw-w-[35%]">
          {skill.skillAssessments.slice(-5).map(ska => (
            <div
              key={ska.sessionId}
              className={`${getColorScore(
                ska.score,
              )} tw-w-[20%] tw-mx-1 tw-rounded tw-flex tw-flex-col tw-items-center tw-h-[90%]`}
            >
              <span className="tw-text-neutral-800 ">Att. {ska.attemptNumber}</span>
              <p className="tw-text-neutral-900 tw-font-semibold tw-my-0 tw-py-0">
                {ska.score === 0 ? '0%' : `${ska.score}0%`}
              </p>
            </div>
          ))}
        </div>
        <div className="tw-flex tw-items-center tw-w-[5%] tw-justify-end tw-cursor-pointer">
          <ChevronRight
            className="tw-text-blue-600"
            onClick={() => {
              setSelectedSkill({
                skillId: `SkillNode:${skill.skillId}`,
                lastSession: skill.skillAssessments.length
                  ? skill.skillAssessments[skill.skillAssessments.length - 1].sessionId
                  : null,
              });
            }}
          />
        </div>
      </div>
      <div className="tw-w-full tw-flex">
        {skill.skillInferredSkills.length > 0 && (
          <div className="tw-w-[5%]">
            <CornerDownRight size={20} className="tw-mt-3 tw-ml-4 tw-text-neutral-600" />
          </div>
        )}

        <div className="tw-w-[95%] tw-grid tw-grid-cols-2 tw-gap-1 tw-mb-2">
          {skill.skillInferredSkills.map(inferredSkill => (
            <div
              key={inferredSkill.code}
              className="tw-w-[100%] tw-flex tw-justify-between tw-items-center tw-border-2 tw-border-solid tw-rounded-xl tw-border-neutral-100 tw-px-3"
            >
              <div className="tw-flex tw-items-center">
                <img
                  className="tw-h-[1.5rem] tw-w-[1.5rem] tw-mr-2"
                  src={inferredSkill.isTargetSkill ? TargetIcon : SupportIcon}
                ></img>{' '}
                <p
                  className="tw-underline tw-text-[1.05rem] cursor-pointer tw-text-blue-600"
                  onClick={() => setSelectedModalSkill({ code: skill.code })}
                >
                  {inferredSkill.name} {`(${inferredSkill.code})`}
                </p>
              </div>

              <div className="tw-border-1 tw-px-3 tw-py-0 tw-rounded-lg tw-border-dashed tw-border-emerald-200">
                <p className="tw-font-semibold tw-py-1 tw-my-0">Passed</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default IndividualReportDailySkill;
